import React, { useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { AppStore } from '../../store/applicationState';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Box from '@mui/material/Box';
import { StylesTable } from '../Stylestable';
import Pagination from '@mui/material/Pagination';
import TextField from '@mui/material/TextField';
import { IProduct } from '../../store/products/types';
import { useSearch } from '../../hooks/useSearch';
import CircularProgress from '@mui/material/CircularProgress';
import { BasicDatePicker, EnhancedTableHead } from '../ui';
import { Order, stableSort } from '../../utils/stableSort';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import IconButton from '@mui/material/IconButton';
import { BoxModal } from '../modals';
import moment from 'moment';
import { GetActions } from '../../store/actions/actions';
import Button from '@mui/material/Button';
import { TooltipCell } from '../ui/TooltipCell';

interface Column {
  id: 'userId' | 'appId' | 'actionId' | 'category1' | 'actionvalue' | 'adate';
  label: string;
  sort?: boolean;
  align?: 'right' | 'left' | 'center';
  width?: string;
  roles?: number[];
}

const sort = true;

const columns: Column[] = [
  { id: 'userId', label: 'Userid', align: 'center', sort },
  { id: 'appId', label: 'AppID', align: 'center', sort },
  { id: 'actionId', label: 'ActionID', align: 'center', sort },
  { id: 'category1', label: 'Category', align: 'center', sort },
  { id: 'actionvalue', label: 'Action value', align: 'center', sort },
  { id: 'adate', label: 'Дата створення події', align: 'center', width: '10%', sort },
];

const initialFilters = {
  bitrixid: '',
  email: '',
  source: '',
  category1: '',
  category2: '',
  actionId: '',
  actionvalue: '',
  adateFrom: null as Date | null,
  adateTo: null as Date | null,
};

type FiltersType = typeof initialFilters;

const DATE_FILTER_FIELDS = ['adateFrom', 'adateTo'];

// Events can be filtered only within the current year and not in the future
const getDateLimits = () => ({
  min: moment().startOf('year').toDate(),
  max: moment().endOf('day').toDate(),
});

const formatDate = (value: Date | null) => (value && moment(value).isValid() ? moment(value).format('YYYY-MM-DD') : '');

const buildRequestData = (filters: FiltersType): { [x: string]: string } => ({
  bitrixid: filters.bitrixid,
  email: filters.email.length >= 3 ? filters.email : '',
  source: filters.source,
  category1: filters.category1,
  category2: filters.category2,
  actionId: filters.actionId,
  actionvalue: filters.actionvalue,
  adateFrom: formatDate(filters.adateFrom),
  adateTo: formatDate(filters.adateTo),
});


type PropsType = {};

export const ListActions: React.FC<PropsType> = () => {
  const { Sidebar } = useSelector((store: AppStore) => store);

  const [tempFilters, setTempFilters] = React.useState(initialFilters);
  const [appliedFilters, setAppliedFilters] = React.useState(initialFilters);
  const [isModal, setIsModal] = React.useState<{ value: string } | null>(null);
  const [order, setOrder] = React.useState<Order>('desc');
  const [orderBy, setOrderBy] = React.useState<keyof IProduct>('absnum');

  const { Actions } = useSelector((store: AppStore) => store);

  const dispatch = useDispatch();
  const classesTable = StylesTable();
  const dateLimits = getDateLimits();

  const { loading, handleSearch, setLoading } = useSearch({
    SearchData: GetActions,
  });

  const handleRequestSort = (event: React.MouseEvent<unknown>, property: keyof IProduct) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleFilterChange = (value: any, field: string) => {
    if (DATE_FILTER_FIELDS.includes(field) && value !== null) {
      if (!moment(value).isValid()) return;
      // Pickers' min/max only restrict the calendar, so validate manually typed dates too
      const min = field === 'adateTo' && tempFilters.adateFrom ? tempFilters.adateFrom : dateLimits.min;
      const max = field === 'adateFrom' && tempFilters.adateTo ? tempFilters.adateTo : dateLimits.max;
      if (!moment(value).isBetween(min, max, 'day', '[]')) return;
    }
    setTempFilters(prev => ({ ...prev, [field]: value }));
  };

  const onSearch = useCallback(() => {
    setAppliedFilters({ ...tempFilters });
    handleSearch('', '', buildRequestData(tempFilters));
  }, [tempFilters, handleSearch]);

  const handleChangePage = (event: unknown, page: number) => {
    if (!Actions.data) return null;
    if (Actions.data.pages >= page) {
      setTempFilters(appliedFilters);

      dispatch(
        GetActions.request({
          data: {
            ...buildRequestData(appliedFilters),
            page,
          },
        })
      );
    }
  };

  const clearFilters = React.useCallback(() => {
    setAppliedFilters(initialFilters)
    setTempFilters(initialFilters)
    setLoading(true)
    handleSearch('', '', {});
  }, [handleSearch, setLoading]);

  React.useEffect(() => {
    clearFilters();
  }, [Sidebar.page.value, clearFilters]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        onSearch();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onSearch]);

  if (loading || Actions.loading) return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
      <CircularProgress color="secondary" />
    </div>;

  return (
    <Box
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '89vh',
        overflow: 'hidden',
      }}
    >
      <Box style={{ display: 'flex', flexWrap: 'wrap',marginTop: '5px'  }}>
        <TextField
          style={{ marginRight: '10px', wordBreak: "break-word", minWidth: "200px" }}
          size="small"
          id="outlined-basic-id"
          label="Userid"
          variant="outlined"
          value={tempFilters.bitrixid}
          className={classesTable.searchField}
          type='number'
          onChange={(e) => {
            handleFilterChange(e.target.value, 'bitrixid');
          }}
        />
        <TextField
          style={{ marginRight: '10px', wordBreak: "break-word", minWidth: "200px" }}
          size="small"
          id="outlined-basic-id-2"
          label="Email (від 3х символів)"
          variant="outlined"
          value={tempFilters.email}
          className={classesTable.searchField}
          onChange={(e) => {
            handleFilterChange(e.target.value, 'email');
          }}
        />
        <TextField
          style={{ marginRight: '10px', wordBreak: "break-word", minWidth: "200px" }}
          size="small"
          id="outlined-basic-id"
          label="Source"
          variant="outlined"
          value={tempFilters.source}
          className={classesTable.searchField}
          onChange={(e) => {
            handleFilterChange(e.target.value, 'source');
          }}
        />
        <TextField
          style={{ marginRight: '10px', wordBreak: "break-word", minWidth: "200px" }}
          size="small"
          id="outlined-basic-id"
          label="ActionId"
          variant="outlined"
          value={tempFilters.actionId}
          className={classesTable.searchField}
          type='number'
          onChange={(e) => {
            handleFilterChange(e.target.value, 'actionId');
          }}
        />
        <TextField
          style={{ marginRight: '10px', wordBreak: "break-word", minWidth: "200px" }}
          size="small"
          id="outlined-basic-id-2"
          label="Category1"
          variant="outlined"
          value={tempFilters.category1}
          type='number'
          className={classesTable.searchField}
          onChange={(e) => {
            handleFilterChange(e.target.value, 'category1');
          }}
        />
        <TextField
          style={{ marginRight: '10px', wordBreak: "break-word", minWidth: "200px" }}
          size="small"
          id="outlined-basic-id-2"
          label="Category2"
          variant="outlined"
          value={tempFilters.category2}
          className={classesTable.searchField}
          type='number'
          onChange={(e) => {
            handleFilterChange(e.target.value, 'category2');
          }}
        />
        <TextField
          style={{ marginRight: '10px', wordBreak: "break-word", minWidth: "200px" }}
          size="small"
          id="outlined-basic-id-2"
          label="Action value"
          variant="outlined"
          value={tempFilters.actionvalue}
          className={classesTable.searchField}
          onChange={(e) => {
            handleFilterChange(e.target.value, 'actionvalue');
          }}
        />
        <div className={classesTable.searchField} style={{ width: '210px', marginRight: '10px' }}>
          <BasicDatePicker
            id="dateFrom"
            label="Дата створення з"
            value={tempFilters.adateFrom}
            minDate={dateLimits.min}
            maxDate={tempFilters.adateTo || dateLimits.max}
            onChange={(value) => {
              handleFilterChange(value, 'adateFrom');
            }}
          />
        </div>
        <div className={classesTable.searchField} style={{ width: '210px' }}>
          <BasicDatePicker
            id="dateTo"
            label="Дата створення до"
            value={tempFilters.adateTo}
            minDate={tempFilters.adateFrom || dateLimits.min}
            maxDate={dateLimits.max}
            onChange={(value) => {
              handleFilterChange(value, 'adateTo');
            }}
          />
        </div>
        <Button variant="contained" color="primary" onClick={clearFilters} style={{ height: '40px', marginRight: '10px' }}>
          Очистити фільтри
        </Button>

        <Button variant="contained" color="primary" onClick={onSearch} style={{ height: '40px' }}>
          Пошук
        </Button>
        {Actions.data && Actions.data.total && Actions.data.total ? (
          <div style={{ height: '40px' }} className={classesTable.textLength}>
            Знайдено {Actions.data.total} подій
          </div>
        ) : (
          ''
        )}
      </Box>
      <TableContainer
        className={classesTable.container}
        style={{
          marginBottom: "40px",
          overflow: 'auto',
        }}>
        <Table className={classesTable.table} stickyHeader aria-label="sticky table">
          <EnhancedTableHead order={order} orderBy={orderBy} onRequestSort={handleRequestSort} columns={columns} />
          <TableBody>
            {Actions.data &&
              stableSort(Actions.data.actions, order, orderBy).map((row, i) => {
                return (
                  <TableRow style={{ maxHeight: 50 }} hover role="checkbox" tabIndex={-1} key={`${i}-${row.absnum}`}>
                    <TooltipCell title={`User Id: ${row.userId}${row.email ? ` | Email: ${row.email}` : ''} `} align="center">
                      {row.userId}
                      <br />
                      {row.email || ''}
                    </TooltipCell>
                    <TooltipCell title={row.appId} align="center">
                      {row.appId}
                      <br />
                      <IconButton aria-label="edit" onClick={() => setIsModal({ value: row.source })}>
                        <RemoveRedEyeIcon />
                      </IconButton>
                    </TooltipCell>
                    <TooltipCell title={`Action Id: ${row.actionId} | Action: ${row.action}`} align="center">
                      {row.actionId}
                      <br />
                      {row.action}
                    </TooltipCell>
                    <TooltipCell
                      align="center"
                      title={`${row.category1 ? `Category1: ${row.category1}` : ''} ${row.category2 ? ` | Category2: ${row.category2}` : ''} `}
                    >
                      {row.category1 ? `Category1: ${row.category1}` : ''}
                      <br />
                      {row.category2 ? `Category2: ${row.category2}` : ''}
                    </TooltipCell>
                    <TooltipCell align="center" style={{ wordBreak: "break-word" }} title={row.actionvalue}>{row.actionvalue}</TooltipCell>
                    <TooltipCell
                      align="center"
                      title={moment(row.adate).format('YYYY-MM-DD HH:mm:ss')}
                    >
                      {moment(row.adate).format('YYYY-MM-DD HH:mm:ss')}
                    </TooltipCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>
      <Box
        style={{
          flexShrink: 0,
          padding: "10px 0px",
          background: " rgb(255, 255, 255)"
        }}
      >
        <Pagination
          className={classesTable.pagination}
          count={Actions.data ? Actions.data.pages : 0}
          page={Actions.data ? Actions.data.currentPage : 1}
          onChange={handleChangePage}
          style={{
            width: "100%",
            bottom: "2vh",
            right: 0,
            flexShrink: 0,
            padding: "10px 0px",
            position: "absolute",
            marginRight: "0vw",
          }}
        />
      </Box>
      {isModal ? <BoxModal open={!!isModal} value={isModal.value} handleClose={() => setIsModal(null)} /> : null}
    </Box>
  );
};
